import { Formik } from 'formik';
import React, { useState } from 'react';
import {
  ILandingPageBuilderForm,
  ILandingPageDesignGroup,
  IUIKitFormData,
  LP_BUILDER_MODAL_STEP,
} from '../../types';
import {
  buildImageField,
  LandingPageBuilderModal,
} from './LandingPageBuilderModal';
import { updateLandingPageDesign } from 'lib/api/superadmin/landingPageDesigns/useEditLandingPageDesignMutation';
import { createLandingPageDesign } from 'lib/api/superadmin/landingPageDesigns/useCreateLandingPageDesignMutation';
import { getInitialLandingPageValues } from '../../helpers/getLandingPageInitialValues';
import {
  IUIKitsQueryParams,
  useInfiniteUIKitsQuery,
} from 'lib/api/superadmin/uikits/useQueryUIKits';
import { DEFAULT_UI_KITS_SIZE } from '../uikit/UIKit';
import { useQuerySingleLandingPageDesign } from 'lib/api/superadmin/landingPageDesigns/useQuerySingleLandingPageDesign';
import { useQueryLandingPageDesignAccess } from 'lib/api/superadmin/landingPageDesigns/useQueryLandingPageDesignAccess';
import { useCurrentAppUrl } from 'lib/hooks/useCurrentAppUrl';
import { useDeleteLandingPageDesignMutation } from 'lib/api/superadmin/landingPageDesigns/useDeleteLandingPageDesignMutation';
import { LoadingIndicator } from 'lib/components';
import { LP_BUILDER_FORM_MODE, LpBuilderFormMode } from '../../constants';
import { usePrepareLandingPageData } from '../../helpers/usePrepareLandingPageData';
import {
  getInitialLandingPageValuesForBulkEdit,
  ILandingPageBuilderBulkEditFormData,
} from '../../helpers/getInitialLandingPageValuesForBulkEdit';
import { FullScreenModal } from 'lib/components/FullScreenModal';
import { useAuth } from 'lib/context';
import { useCDK } from 'lib/hooks/useCdk';
import { getValidationSchema } from '../../helpers/getValidationSchema';

interface LandingPageBuilderModalFormProps {
  onSubmit: (
    values: ILandingPageBuilderForm | ILandingPageBuilderBulkEditFormData
  ) => Promise<void>;
  isSubmitting: boolean;
  mode: LpBuilderFormMode;
  selectedTemplateDesignId: number | null;
  previewLandingPageId: number;
  setPreviewLandingPageId: React.Dispatch<React.SetStateAction<number>>;
  setStep: React.Dispatch<React.SetStateAction<LP_BUILDER_MODAL_STEP>>;
  step: LP_BUILDER_MODAL_STEP;
  group?: ILandingPageDesignGroup;
  onClose: () => Promise<void>;
  isResellerAdmin: boolean;
  isCovideoSuperAdmin: boolean;
}

export const LandingPageBuilderModalForm = ({
  mode,
  selectedTemplateDesignId,
  previewLandingPageId,
  setPreviewLandingPageId,
  setStep,
  step,
  group,
  onSubmit,
  isSubmitting,
  onClose,
  isResellerAdmin,
  isCovideoSuperAdmin,
}: LandingPageBuilderModalFormProps) => {
  const { userData } = useAuth();
  const { isCdkUser } = useCDK();
  const { landingPageUrl } = useCurrentAppUrl();
  const { prepare, isPreparing, isGeneratingAssets } =
    usePrepareLandingPageData();
  const isBulkEditMode = mode === LP_BUILDER_FORM_MODE.BULK_EDIT;

  const [searchUIKitsQuery, setSearchUIKitsQuery] = useState('');
  const [isPreparingDataForPreview, setIsPreparingDataForPreview] =
    useState(false);

  // DELETE Design Mutation
  const { mutateAsync: deleteDesignMutation } =
    useDeleteLandingPageDesignMutation({
      showToastNotifications: false,
    });

  // GET UI Kits
  const uiKitsQueryParams: IUIKitsQueryParams = {
    limit: DEFAULT_UI_KITS_SIZE,
    search: searchUIKitsQuery,
  };

  const {
    data: uiKitsInfinite,
    fetchNextPage,
    isLoading: isLoadingUIKits,
  } = useInfiniteUIKitsQuery(uiKitsQueryParams);

  const totalCount = +(uiKitsInfinite?.pages?.[0]?.count ?? 0);
  const pageTotal =
    uiKitsInfinite?.pages?.reduce(
      (acc, page) => acc + page?.data?.length || 0,
      0
    ) || 0;

  const uiKitsResponse: IUIKitFormData[] =
    uiKitsInfinite?.pages.flatMap(page =>
      page.data.map(item => ({
        ...item,
        thumbnail: buildImageField(null, item.thumbnail),
      }))
    ) ?? [];

  // GET Template Design
  const {
    data: templateDesign,
    isLoading: isLoadingTemplateDesign,
    isFetching: isFetchingTemplateDesign,
  } = useQuerySingleLandingPageDesign(
    selectedTemplateDesignId,
    !!selectedTemplateDesignId
  );

  // GET Template Design Access
  const {
    data: templateDesignAccess,
    isLoading: isLoadingTemplateDesignAccess,
    isFetching: isFetchingTemplateDesignAccess,
  } = useQueryLandingPageDesignAccess(
    selectedTemplateDesignId,
    !!selectedTemplateDesignId
  );

  const handleCreateDataForPreview = async (
    values: ILandingPageBuilderForm
  ) => {
    const payload = await prepare(values);

    return selectedTemplateDesignId
      ? updateLandingPageDesign({
          id: selectedTemplateDesignId,
          ...payload,
        })
      : createLandingPageDesign(payload);
  };

  const deleteAndResetPreviewLandingPageDesign = async () => {
    await deleteDesignMutation({ templateId: previewLandingPageId });
    setPreviewLandingPageId(0);
  };

  const handleOpenBuilderPreview = async (
    values: ILandingPageBuilderForm,
    showSalesContent: boolean
  ) => {
    setIsPreparingDataForPreview(true);
    if (!!previewLandingPageId && !selectedTemplateDesignId) {
      // delete previous preview
      await deleteAndResetPreviewLandingPageDesign();
    }
    const data = await handleCreateDataForPreview(values);
    if (data) {
      // set new preview id
      setPreviewLandingPageId(data.id);
      setIsPreparingDataForPreview(false);
      window.open(
        `${landingPageUrl}preview/${data.id}?showSalesContent=${showSalesContent}`,
        '_blank'
      );
    }
  };

  const isLoading =
    isSubmitting ||
    isFetchingTemplateDesign ||
    isLoadingTemplateDesignAccess ||
    isFetchingTemplateDesignAccess ||
    isLoadingTemplateDesign ||
    isPreparing;

  const isFormDataLoading =
    isLoadingTemplateDesign ||
    isLoadingTemplateDesignAccess ||
    isFetchingTemplateDesignAccess ||
    isFetchingTemplateDesign;

  const validationSchema = getValidationSchema(mode);

  // null values are used for bulk edit mode since we need all the properties to be empty
  const initialValues = isBulkEditMode
    ? getInitialLandingPageValuesForBulkEdit()
    : getInitialLandingPageValues({
        uiKitsResponse,
        userData,
        isCdkUser,
        templateDesign,
      });

  return (
    <FullScreenModal>
      {isFormDataLoading ? (
        <div>
          <LoadingIndicator isLoading={isFormDataLoading} />
        </div>
      ) : (
        <Formik
          onSubmit={onSubmit}
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnMount
        >
          {() => {
            return (
              <LandingPageBuilderModal
                selectedTemplateDesignId={selectedTemplateDesignId}
                templateDesignAccess={templateDesignAccess}
                totalCount={totalCount}
                pageTotal={pageTotal}
                uiKitsQueryParams={uiKitsQueryParams}
                onClose={onClose}
                fetchNextPage={fetchNextPage}
                isLoading={isLoading}
                isGeneratingAssets={isGeneratingAssets}
                isLoadingUIKits={isLoadingUIKits}
                isPreparingDataForPreview={isPreparingDataForPreview}
                step={step}
                setStep={setStep}
                mode={mode}
                groupTitle={group?.title}
                searchUIKitsQuery={searchUIKitsQuery}
                setSearchUIKitsQuery={setSearchUIKitsQuery}
                handleOpenBuilderPreview={handleOpenBuilderPreview}
                uiKitsResponse={uiKitsResponse}
                isCovideoSuperAdmin={isCovideoSuperAdmin}
                isResellerAdmin={isResellerAdmin}
              />
            );
          }}
        </Formik>
      )}
    </FullScreenModal>
  );
};
